import { createFileRoute, Link } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import {
	ArrowLeft,
	Coins,
	History,
	Pencil,
	SlidersHorizontal,
} from "lucide-react"
import { CurrencyAmountsTable } from "@/components/coupons/currency-amounts-table"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	Section,
	Stat,
	StatStrip,
} from "@/components/layout"
import { BACK_LINK_CLASS } from "@/components/promotions/back-link"
import {
	DetailColumn,
	DetailLayout,
	DetailPage,
	DetailPageHeader,
} from "@/components/promotions/detail-page"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import { Switch } from "@/components/ui/switch"
import { COUPONS_SEARCH_DEFAULTS } from "@/config/search-defaults"
import { BrazeSnippetCard } from "@/features/coupons-admin/BrazeSnippetCard"
import { formatProgramDiscount } from "@/features/coupons-admin/CouponProgramsGrid"
import { ProgramCouponsSubGrid } from "@/features/coupons-admin/ProgramCouponsSubGrid"
import {
	useCouponProgram,
	useSetCouponProgramActive,
} from "@/queries/marketplace"
import type { AdminCouponProgramDetail } from "@/types/marketplace.types"

export const Route = createFileRoute("/coupons/programs/$programId/")({
	component: CouponProgramDetailPage,
})

function formatCurrency(cents: number, currency: string): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100)
}

function regionLabel(region: string | null | undefined): string {
	if (!region) return "All regions"
	return region === "NA" ? "North America" : "Europe"
}

function ProgramSummary({ program }: { program: AdminCouponProgramDetail }) {
	const hasCurrencyAmounts = program.currencyAmounts.length > 0
	const exampleCode = program.codePrefix
		? `${program.codePrefix}-7QK4M2ND`
		: "7QK4M2ND"

	return (
		<DetailColumn>
			<DetailPanel title="Rules" icon={SlidersHorizontal}>
				<DetailPanelSection label="Discount">
					<KeyValueList>
						<KeyValueItem label="Type">
							{program.type === "percentage" ? "Percentage" : "Fixed amount"}
						</KeyValueItem>
						{program.type === "percentage" && (
							<KeyValueItem label="Percentage">{program.value}%</KeyValueItem>
						)}
						<KeyValueItem label="Discount">
							{formatProgramDiscount(program)}
						</KeyValueItem>
						{!hasCurrencyAmounts && program.minimumOrderAmount != null && (
							<KeyValueItem label="Minimum order">
								{formatCurrency(program.minimumOrderAmount, "EUR")}
							</KeyValueItem>
						)}
						{!hasCurrencyAmounts && program.maximumDiscountAmount != null && (
							<KeyValueItem label="Maximum discount">
								{formatCurrency(program.maximumDiscountAmount, "EUR")}
							</KeyValueItem>
						)}
					</KeyValueList>
				</DetailPanelSection>
				<DetailPanelSection label="Codes and validity">
					<KeyValueList>
						<KeyValueItem label="Prefix">
							{program.codePrefix ? (
								<code className="font-mono">{program.codePrefix}</code>
							) : (
								<span className="text-meta">None (random codes)</span>
							)}
						</KeyValueItem>
						<KeyValueItem label="Example code">
							<code className="font-mono">{exampleCode}</code>
						</KeyValueItem>
						<KeyValueItem label="Validity">
							{program.validityDays} day{program.validityDays === 1 ? "" : "s"}{" "}
							from issuance
						</KeyValueItem>
						<KeyValueItem label="Uses per coupon">1</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
				<DetailPanelSection label="Targeting">
					<KeyValueList>
						<KeyValueItem label="Region">
							{regionLabel(program.region)}
						</KeyValueItem>
						<KeyValueItem label="New buyers only">
							{program.newBuyersOnly ? "Yes" : "No"}
						</KeyValueItem>
						<KeyValueItem label="Combinable">
							{program.combinable ? "Yes" : "No"}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
			<DetailPanel title="Record" icon={History}>
				<DetailPanelSection>
					<KeyValueList>
						<KeyValueItem label="Created">
							{format(new Date(program.createdAt), "PPP p")}
						</KeyValueItem>
						<KeyValueItem label="Last updated">
							{formatDistanceToNow(new Date(program.updatedAt), {
								addSuffix: true,
							})}
						</KeyValueItem>
						<KeyValueItem label="Created by">
							{program.createdByUsername ? (
								<span>@{program.createdByUsername}</span>
							) : (
								<span className="font-mono">{program.createdBy ?? "None"}</span>
							)}
						</KeyValueItem>
					</KeyValueList>
				</DetailPanelSection>
			</DetailPanel>
		</DetailColumn>
	)
}

function CouponProgramDetailPage() {
	const { programId } = Route.useParams()
	const { program, isLoading, error } = useCouponProgram({ programId })
	const setActiveMutation = useSetCouponProgramActive()

	if (isLoading) {
		return (
			<DetailPage>
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-[78px]" />
				<DetailLayout>
					<div className="flex flex-col gap-3">
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
					<Skeleton className="h-80" />
				</DetailLayout>
			</DetailPage>
		)
	}

	if (error || !program) {
		return (
			<DetailPage className="items-start gap-3">
				<p className="text-bad">
					{error?.message || "The coupon program could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/coupons/programs">
						<ArrowLeft />
						Back to programs
					</Link>
				</Button>
			</DetailPage>
		)
	}

	return (
		<DetailPage>
			<DetailPageHeader
				back={
					<Link to="/coupons/programs" className={BACK_LINK_CLASS}>
						<ArrowLeft />
						Programs
					</Link>
				}
				title={program.name}
				badges={
					<>
						<code className="font-mono text-sm break-all text-muted-foreground">
							{program.key}
						</code>
						{program.isActive ? (
							<StatusDot tone="ok">Active</StatusDot>
						) : (
							<StatusDot>Paused</StatusDot>
						)}
					</>
				}
				description={program.description || undefined}
				actions={
					<>
						<div className="flex items-center gap-2">
							<Label htmlFor="program-active">Issuing</Label>
							<Switch
								id="program-active"
								checked={program.isActive}
								disabled={setActiveMutation.isPending}
								onCheckedChange={(checked) =>
									setActiveMutation.mutate({
										id: program.id,
										isActive: checked,
									})
								}
							/>
						</div>
						<Button variant="outline" asChild>
							<Link
								to="/coupons/programs/$programId/edit"
								params={{ programId: program.id }}
							>
								<Pencil />
								Edit
							</Link>
						</Button>
					</>
				}
			/>

			<StatStrip>
				<Stat
					label="Issued"
					value={program.stats.issued.toLocaleString()}
					detail="coupons minted"
					muted={program.stats.issued === 0}
				/>
				<Stat
					label="Redeemed"
					value={program.stats.redeemed.toLocaleString()}
					detail="confirmed redemptions"
					muted={program.stats.redeemed === 0}
				/>
				<Stat
					label="Still active"
					value={program.stats.active.toLocaleString()}
					detail="unexpired and unused"
					muted={program.stats.active === 0}
				/>
			</StatStrip>

			<DetailLayout>
				<DetailColumn>
					{program.currencyAmounts.length > 0 && (
						<DetailPanel
							title={
								program.type === "fixed_amount"
									? "Per-currency amounts"
									: "Per-currency settings"
							}
							icon={Coins}
							flush
						>
							<CurrencyAmountsTable
								rows={program.currencyAmounts}
								showAmount={program.type === "fixed_amount"}
								bare
							/>
						</DetailPanel>
					)}

					<Section
						label="Issued coupons"
						action={
							<Link
								to="/coupons"
								search={{
									...COUPONS_SEARCH_DEFAULTS,
									programId: program.id,
									source: "all",
								}}
								className="text-xs text-primary-text hover:underline"
							>
								Open in coupons grid
							</Link>
						}
					>
						{program.stats.issued === 0 ? (
							<p className="text-muted-foreground">
								No coupons issued yet. Braze will mint them as the canvas sends.
							</p>
						) : (
							<ProgramCouponsSubGrid programId={program.id} />
						)}
					</Section>

					<BrazeSnippetCard programKey={program.key} />
				</DetailColumn>

				<ProgramSummary program={program} />
			</DetailLayout>
		</DetailPage>
	)
}
